/**
 * Optional inner node: scroll math uses the title/lead band instead of the
 * whole section (avoids a tall `min-h` on `Section` skewing the scroll).
 */
const LANDING_SECTION_SCROLL_ANCHOR_SELECTOR = "[data-landing-scroll-anchor]";

/**
 * Pixels above viewport vertical center to place a section’s midpoint when
 * there is no inner scroll anchor.
 */
const SCROLL_TO_SECTION_NUDGE_ABOVE_CENTER_PX = 44;

/**
 * When a scroll anchor exists, viewport Y for the **top** of the anchor. Higher
 * = less scroll and a lower title on screen. Tuned so the H2 is not stuck in
 * the upper third with empty space only below the video.
 */
const LANDING_SCROLL_ANCHOR_TOP_VIEWPORT_RATIO = 0.1;

/**
 * Resolves the DOM node to use for scroll metrics: inner anchor if set, else
 * the section root.
 */
function resolveScrollFocusElement(params: { sectionElement: HTMLElement }): HTMLElement {
  const { sectionElement } = params;
  const anchor = sectionElement.querySelector<HTMLElement>(LANDING_SECTION_SCROLL_ANCHOR_SELECTOR);
  if (anchor) {
    return anchor;
  }
  return sectionElement;
}

/**
 * Smoothly scrolls to a section by ID. Sections with `data-landing-scroll-anchor`
 * place that node’s top at the chosen viewport height; other sections use a
 * slightly above-center position for the whole block.
 */
export function scrollToSection(sectionId: string) {
  const element = document.getElementById(sectionId);
  if (!element) return;

  const focusElement = resolveScrollFocusElement({ sectionElement: element });
  const rect = focusElement.getBoundingClientRect();
  const focusTopY = rect.top + window.scrollY;
  const isInnerAnchor = focusElement !== element;

  let targetScrollTop: number;
  if (isInnerAnchor) {
    targetScrollTop = focusTopY - window.innerHeight * LANDING_SCROLL_ANCHOR_TOP_VIEWPORT_RATIO;
  } else {
    const elementCenterY = focusTopY + rect.height / 2;
    const focusY = window.innerHeight / 2 - SCROLL_TO_SECTION_NUDGE_ABOVE_CENTER_PX;
    targetScrollTop = elementCenterY - focusY;
  }

  const maxScrollTop = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
  if (targetScrollTop < 0) {
    targetScrollTop = 0;
  } else if (targetScrollTop > maxScrollTop) {
    targetScrollTop = maxScrollTop;
  }

  window.scrollTo({
    behavior: "smooth",
    top: targetScrollTop,
  });
}
